import React, { useCallback, useRef, useState } from 'react';

import { usePriceRowContext } from '../../../price-row-context';

import { ExpandButton } from '../../components/expand-button';
import { InfoModal } from '../../components/info-modal';
import { ReportButton } from '../../components/report-button';
import { StoreInfo } from '../../components/store-info';
import { fetchSotyAwardsByStoreId } from '../../requests';
import { AlternativePriceRow } from '../alternative-price-row';
import { FeaturedColumns } from '../../components/price-row-columns/featured-columns';
import { UnfeaturedColumns } from '../../components/price-row-columns/unfeatured-columns';

import { trackExpandPriceRowClick } from '../../tracking';

import { setTableLinkProps, logRevenueEvent } from '../../utils';

import type { Award } from 'src/types';
import type { ProductPriceClickStatus } from 'src/areas/product/actions/types';
import type { IRegularPriceRow } from '../../types';
import { ViewSize, ReportRowType } from '../../types';

import { Row, StyledCard } from '../styles';
import {
  StyledAlternativePricesArea,
  StyledExpandButtonWrapper,
  StyledInfoRow,
} from './styles';

export const RegularPriceRow: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<IRegularPriceRow>>
> = props => {
  const { onClickOutBounce, onPriceClick, className } = props;

  const [showDisabledInfo, setShowDisabledInfo] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [priceRowReport, setPriceRowReport] = useState(ReportRowType.NONE);
  const [sotyBadge, setSotyBadge] = useState<Award | undefined | null>();
  const [reportRowId, setReportRowId] = useState<string | null>(null);
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);

  const priceRowRef = useRef<HTMLDivElement>(null);

  const {
    isFeatured = false,
    offer,
    product,
    isInPriceList,
    isOurChoice,
    viewSize,
  } = usePriceRowContext();

  const { price, priceId, store, stock, externalUri, alternativePrices } =
    offer;

  const startBounceTimer = useCallback(
    (id: string) => {
      // Start a timer when a user clicks out on a product
      // if they return to Prisjakt within 15 seconds
      // display an error report button in the price row.

      const startTime = Date.now();

      const onRefocus = () => {
        if (
          Date.now() - startTime > 15000 &&
          document.visibilityState === 'visible'
        ) {
          document.removeEventListener('visibilitychange', onRefocus);
          return;
        }

        if (document.visibilityState === 'visible') {
          setReportRowId(id);

          // Action event for tracking
          onClickOutBounce(product.id, priceId);

          document.removeEventListener('visibilitychange', onRefocus);
        }
      };

      document.addEventListener('visibilitychange', onRefocus);
    },
    [onClickOutBounce, priceId, product.id, setReportRowId]
  );

  const handleClickOut = useCallback(
    (
      e: React.MouseEvent<Element, MouseEvent>,
      id: string | null | undefined,
      isAlternative = false
    ) => {
      const isInternational = offer?.store?.market === 'secondary';
      const status = getGAStatus(isInternational, isAlternative);

      setPriceRowReport(
        isAlternative ? ReportRowType.ALTERNATIVE : ReportRowType.DEFAULT
      );
      if (isFeatured && id) {
        logRevenueEvent(id, product.id);
      }

      if (externalUri) {
        if (IS_BROWSER && id) {
          startBounceTimer(id);
        }

        if (!showDisabledInfo && !e.defaultPrevented) {
          onPriceClick(isFeatured, status, isAlternative);
        }
      } else {
        e.preventDefault();
        setShowDisabledInfo(!showDisabledInfo);
      }
    },
    [
      isFeatured,
      externalUri,
      offer,
      product.id,
      showDisabledInfo,
      startBounceTimer,
      onPriceClick,
    ]
  );

  const getTableLinkProps = (isAlternative?: boolean) =>
    setTableLinkProps(
      isFeatured,
      (e: React.MouseEvent) => handleClickOut(e, priceId, isAlternative),
      externalUri
    );

  const handleExpandButtonClick = (
    event: React.MouseEvent<Element, MouseEvent>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (viewSize === ViewSize.SMALL) {
      setInfoModalOpen(state => !state);
    } else {
      setExpanded(!expanded);
    }

    if (!infoModalOpen && !expanded) {
      trackExpandPriceRowClick(
        product.id,
        product.name,
        store?.id,
        store?.name
      );

      if (sotyBadge === undefined && store?.id) {
        fetchSotyAwardsByStoreId(store.id).then(response => {
          if (response?.data?.store?.storeOfTheYearAwards?.length) {
            setSotyBadge(response.data.store.storeOfTheYearAwards[0]);
          } else {
            setSotyBadge(null);
          }
        });
      }
    }
  };

  const closeReportOverlay = useCallback(() => {
    setPriceRowReport(ReportRowType.NONE);
    setReportRowId(null);
  }, [setReportRowId]);

  return (
    <React.Fragment key={store?.id || 'price-row'}>
      <StyledCard
        className={className}
        containerProps={{ ref: priceRowRef }}
        $expanded={expanded}
        $featured={isFeatured}
        $extraMargin={isOurChoice && isInPriceList}
      >
        <Row
          className="pj-ui-price-row" // Used to track click out (see out.js)
          data-test="PriceRow"
          $expanded={expanded}
          $featured={isFeatured}
          $hasAuthorizedDealer={Boolean(offer.authorizedDealer)}
          $noExternalUri={!externalUri}
        >
          {isInPriceList && (
            <StyledExpandButtonWrapper
              onClick={handleExpandButtonClick}
              $expanded={expanded}
            >
              <ExpandButton expanded={expanded} />
            </StyledExpandButtonWrapper>
          )}

          {isFeatured && (
            <FeaturedColumns tableLinkProps={getTableLinkProps()} />
          )}

          {!isFeatured && (
            <UnfeaturedColumns
              priceRowRef={priceRowRef}
              setShowDisabledInfo={setShowDisabledInfo}
              showDisabledInfo={showDisabledInfo}
              tableLinkProps={getTableLinkProps()}
            />
          )}
        </Row>

        {infoModalOpen && (
          <InfoModal
            alternativePrices={alternativePrices}
            closeModal={() => setInfoModalOpen(false)}
            closeReportOverlay={closeReportOverlay}
            isOpen={infoModalOpen}
            priceRowReport={priceRowReport}
            reportRowId={reportRowId}
            sotyBadge={sotyBadge}
            tableLinkProps={getTableLinkProps(true)}
          />
        )}

        {expanded && store && (
          <StyledInfoRow data-test="ColumnExpanded">
            <StoreInfo sotyBadge={sotyBadge} />
          </StyledInfoRow>
        )}

        {expanded && alternativePrices && (
          <StyledAlternativePricesArea>
            {alternativePrices.map(altPrice => {
              if (!altPrice) return null;

              return (
                <React.Fragment key={altPrice.id || 'alternative-price-row'}>
                  <AlternativePriceRow
                    alternativeOffer={altPrice}
                    priceRowRef={priceRowRef}
                    tableLinkProps={getTableLinkProps(true)}
                  />
                  {priceRowReport === ReportRowType.ALTERNATIVE &&
                    reportRowId === altPrice.id && (
                      <ReportButton
                        priceId={altPrice.id}
                        priceValue={
                          altPrice.price && altPrice.price.exclShipping
                        }
                        stockStatus={altPrice.stock && altPrice.stock.status}
                        onClose={closeReportOverlay}
                        productName={altPrice.name}
                        isAlternative
                      />
                    )}
                </React.Fragment>
              );
            })}
          </StyledAlternativePricesArea>
        )}
      </StyledCard>
      {isInPriceList &&
        priceRowReport === ReportRowType.DEFAULT &&
        reportRowId === priceId && (
          <ReportButton
            priceId={priceId}
            priceValue={price && price.exclShipping}
            stockStatus={stock && stock.status}
            onClose={closeReportOverlay}
            productName={product.name}
          />
        )}
    </React.Fragment>
  );
};

function getGAStatus(
  isInternational: boolean,
  isAlternative: boolean
): ProductPriceClickStatus {
  if (isInternational) {
    return 'international';
  }
  if (isAlternative) {
    return 'variant';
  }
  return '';
}
